import React, { SetStateAction } from 'react';
import { Flex } from 'antd';
import classNames from 'classnames';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { formatCurrency } from '@shared/utils/currency';
import { Values } from '@shared/utils/objectEnums';
import { FuelTypes } from '@shared/utils/constants';
import normalizeNumber from '@shared/utils/normalizeNumber';
import { formatAndRoundNumber } from '@shared/utils/formatNumber';
import { ensureNegative } from '@shared/utils/math';
import type { GetEstimateDetailsResponse } from '@api/features/estimates/getEstimateDetails';
import type { CargoDetails } from '@api/features/cargos/getCargoDetails';
import Button from '@client/components/Button';
import type { ExpandedRows } from '@client/screens/estimates/details/components/EstimatedPnL';
import styles from '@client/screens/estimates/details/components/styles/EstimatedPnL.module.css';
import TooltipIcon from '@client/components/TooltipIcon';
import TargetControls from '../components/TargetControls';
import { formatEuEtsEmissionCost } from './formatEuEtsEmissionCost';
import { formatFreightRate } from './formatFreightRate';

type FuelType = Values<typeof FuelTypes>;

export const BUNKERS_BELOW_ZERO_WARNING = 'Forecast bunkers ROB go below zero. When forecast bunkers go below zero, ' +
	'the bunker expenditure calculation stops. You need to add a planned stem ' +
	'to see an accurate bunker expenditure calculation.';

export type GetEstimatedVcPnlItemsParams = {
	estimate: GetEstimateDetailsResponse;
	onSetTargetTce: (targetTce: number) => void;
	onLockTce: () => void;
	onSetTargetNetResult: (targetNetResult: number) => void;
	onLockNetResult: () => void;
	cargos: CargoDetails[];
	expanded: ExpandedRows;
	setExpanded: React.Dispatch<SetStateAction<ExpandedRows>>;
	tceDiff: number;
	missingBunkerQuantities: Array<Values<typeof FuelTypes>>;
	bunkersGoBelowZero: boolean;
	hideTceControls?: boolean;
	hasCargoRelet: boolean;
	disabled?: boolean;
};

const getEstimatedVcPnlItems = ({
	estimate,
	onSetTargetTce,
	onLockTce,
	onSetTargetNetResult,
	onLockNetResult,
	cargos,
	expanded,
	setExpanded,
	tceDiff,
	hideTceControls = false,
	hasCargoRelet,
	disabled,
	missingBunkerQuantities,
	bunkersGoBelowZero,
}: GetEstimatedVcPnlItemsParams) => {
	const bunkerExpenses = estimate.bunkerExpenses ?? {};
	const showTargetControls = !hideTceControls && !hasCargoRelet;
	const breakevenCargo = cargos.length === 1 ? cargos[0] : null;

	const updateExpanded = (key: keyof ExpandedRows) => {
		setExpanded((prev) => ({
			...prev,
			[key]: !prev[key],
		}));
	};

	const getReletRevenueItems = (estimate: GetEstimateDetailsResponse) => [
		{
			label: 'Freight VC-Out',
			span: 3,
			children: (
				<span data-testid="freight-vc-out">
					{formatCurrency(
						estimate.result.freightVcOut ?? estimate.result.freight ?? 0,
						estimate.currency,
					)}
				</span>
			),
		},
		{
			label: 'Freight VC-In',
			span: 3,
			children: (
				<span data-testid="freight-vc-in">
					{formatCurrency(estimate.result.freightVcIn ?? 0, estimate.currency)}
				</span>
			),
		},
		{
			label: 'Misc. Revenue',
			span: 3,
			children: (
				<span data-testid="misc-revenue">
					{formatCurrency(
						estimate.result.miscRevenue ?? estimate.result.otherRevenue ?? 0,
						estimate.currency,
					)}
				</span>
			),
		},
		{
			label: 'Demurrage VC-Out',
			span: 3,
			children: (
				<span data-testid="demurrage-vc-out">
					{formatCurrency(
						estimate.result.demurrageVcOut ?? estimate.result.demurrage ?? 0,
						estimate.currency,
					)}
				</span>
			),
		},
		{
			label: 'Demurrage VC-In',
			span: 3,
			children: (
				<span data-testid="demurrage-vc-in">
					{formatCurrency(estimate.result.demurrageVcIn ?? 0, estimate.currency)}
				</span>
			),
		},
		{
			label: 'Despatch VC-Out',
			span: 3,
			children: (
				<span data-testid="despatch-vc-out">
					{formatCurrency(
						estimate.result.despatchVcOut ?? estimate.result.despatch ?? 0,
						estimate.currency,
					)}
				</span>
			),
		},
		{
			label: 'Despatch VC-In',
			span: 3,
			children: (
				<span data-testid="despatch-vc-in">
					{formatCurrency(estimate.result.despatchVcIn ?? 0, estimate.currency)}
				</span>
			),
		},
		{
			label: 'Address Commissions VC-Out',
			span: 3,
			children: (
				<span data-testid="address-commission-vc-out">
					{formatCurrency(estimate.result.addressCommissionVcOut ?? 0, estimate.currency)}
				</span>
			),
		},
		{
			label: 'Address Commissions VC-In',
			span: 3,
			children: (
				<span data-testid="address-commission-vc-in">
					{formatCurrency(estimate.result.addressCommissionVcIn ?? 0, estimate.currency)}
				</span>
			),
		},
		{
			label: 'Broker Commissions VC-Out',
			span: 3,
			children: (
				<span data-testid="broker-commission-vc-out">
					{formatCurrency(estimate.result.brokerCommissionVcOut ?? 0, estimate.currency)}
				</span>
			),
		},
		{
			label: 'Broker Commissions VC-In',
			span: 3,
			children: (
				<span data-testid="broker-commission-vc-in">
					{formatCurrency(estimate.result.brokerCommissionVcIn ?? 0, estimate.currency)}
				</span>
			),
		},
	];

	const getRegularRevenueItems = (estimate: GetEstimateDetailsResponse) => [
		{
			label: 'Freight',
			span: 3,
			children: (
				<span data-testid="freight">
					{formatCurrency(estimate.result.freight ?? 0, estimate.currency)}
				</span>
			),
		},
		{
			label: 'Other Revenue',
			span: 3,
			children: (
				<span data-testid="other-revenue">
					{formatCurrency(estimate.result.otherRevenue ?? 0, estimate.currency)}
				</span>
			),
		},
		{
			label: 'Demurrage',
			span: 3,
			children: (
				<span data-testid="demurrage">
					{formatCurrency(estimate.result.demurrage ?? 0, estimate.currency)}
				</span>
			),
		},
		{
			label: 'Despatch',
			span: 3,
			children: (
				<span data-testid="despatch">
					{formatCurrency(estimate.result.despatch ?? 0, estimate.currency)}
				</span>
			),
		},
		{
			label: 'Address Commissions',
			span: 3,
			children: (
				<span data-testid="address-commissions">
					{formatCurrency(estimate.result.addressCommissions ?? 0, estimate.currency)}
				</span>
			),
		},
		{
			label: 'Broker Commissions',
			span: 3,
			children: (
				<span data-testid="broker-commissions">
					{formatCurrency(estimate.result.brokerCommissions ?? 0, estimate.currency)}
				</span>
			),
		},
	];

	let revenueItems = getRegularRevenueItems(estimate);

	if (hasCargoRelet) {
		revenueItems = getReletRevenueItems(estimate);
	}

	return [
		...revenueItems,
		{
			label: (<b>Net Revenue</b>),
			span: 3,
			children: (
				<b data-testid="net-revenue">
					{formatCurrency(estimate.result.netRevenue ?? 0, estimate.currency)}
				</b>
			),
		},
		...(!hasCargoRelet ? [
			{
				label: (
					<Flex justify="space-between">
						Port Costs
						<Button
							type="link"
							size="small"
							onClick={() => updateExpanded('portCosts')}
							data-testid="expand-port-costs"
						>
							{expanded.portCosts ? 'Hide' : 'Expand'}
						</Button>
					</Flex>
				),
				span: 3,
				children: (
					<span data-testid="port-costs">
						{formatCurrency(
							normalizeNumber(estimate.result.totalPortCosts),
							estimate.currency,
						)}
					</span>
				),
			},
			...(expanded.portCosts ? [
				{
					label: '- DAs',
					span: 3,
					children: (
						<span data-testid="port-costs-das">
							{formatCurrency(estimate.result.portCharge ?? 0, estimate.currency)}
						</span>
					),
				},
				{
					label: '- Liner Costs',
					span: 3,
					children: (
						<span data-testid="port-costs-liner">
							{formatCurrency(estimate.result.linerCosts ?? 0, estimate.currency)}
						</span>
					),
				},
			] : []),
		] : []),
		...(!hasCargoRelet ? [{
			label: (
				<Flex justify="space-between">
					<Flex>
						Bunker Exp.
						{missingBunkerQuantities.length > 0 && (
							<TooltipIcon 
								iconProps={{ style: { color: 'red' } }}
								Icon={ExclamationCircleOutlined}
							>
								Bunker quantities missing for one or more fuel grades
							</TooltipIcon>
						)}
						{bunkersGoBelowZero && (
							<TooltipIcon
								iconProps={{ style: { color: 'red' } }}
								Icon={ExclamationCircleOutlined}
								testId="bunkers-below-zero-warning"
							>
								{BUNKERS_BELOW_ZERO_WARNING}
							</TooltipIcon>
						)}
					</Flex>
					<Button
						type="link"
						size="small"
						onClick={() => updateExpanded('bunkers')}
						data-testid="expand-bunker-costs"
					>
						{expanded.bunkers ? 'Hide' : 'Expand'}
					</Button>
				</Flex>
			),
			span: 3,
			children: (
				<span data-testid="bunker-expense">
					{formatCurrency(estimate.result.bunkerExpense ?? 0, estimate.currency)}
				</span>
			),
		},
		...(expanded.bunkers ? Object.keys(bunkerExpenses)
			.map((key) => {
				const isMissing = missingBunkerQuantities.find((f) => f === key) != null;

				return ({
					key,
					label: (
						<Flex>
							-{formatAndRoundNumber(Math.abs(bunkerExpenses[key as FuelType]?.consumption ?? 0), 0)}MT {key}
							{isMissing && (
								<TooltipIcon
									iconProps={{ style: { color: 'red' } }}
									Icon={ExclamationCircleOutlined}
								>
									Bunker quantities missing for this grade
								</TooltipIcon>
							)}
						</Flex>
					),
					span: 3,
					children: (
						<span data-testid={`bunker-expense-${key.toLowerCase()}`}>
							{formatCurrency(
								bunkerExpenses[key as FuelType]?.expenditure ?? 0,
								estimate.currency
							)}
						</span>
					),
				});
			}) : []),
		] : []),
		...(!hasCargoRelet ? [{
			label: (
				<Flex justify="space-between">
					{estimate.isIceClass ? 'EU ETS Costs (-5%)' : 'EU ETS Costs'}
					<Button
						type="link"
						size="small"
						onClick={() => updateExpanded('euEts')}
						data-testid="expand-eua-costs"
					>
						{expanded.euEts ? 'Hide' : 'Expand'}
					</Button>
				</Flex>
			),
			span: 3,
			children: (
				<span data-testid="eu-ets-costs">
					{formatCurrency(estimate.result.totalEUAcost ?? 0, estimate.currency)}
				</span>
			),
		},
		...(expanded.euEts ? [{
			label: ` - At Sea: ${formatAndRoundNumber(estimate.result.totalEligibleEmissionsAtSea ?? 0, 0)} MT CO₂`,
			span: 3,
			children: (
				<span data-testid="eu-ets-at-sea">
					{formatEuEtsEmissionCost(
						estimate.euaPriceOverride ?? estimate.remoteEuaPrice,
						ensureNegative(estimate.result?.factorAdjustedEligibleEmissionsAtSea ?? 0),
						estimate.currency,
					)}
				</span>
			),
		},
		{
			label: ` - In Port: ${formatAndRoundNumber(estimate.result.totalEligibleEmissionsInPort ?? 0, 0)} MT CO₂`,
			span: 3,
			children: (
				<span data-testid="eu-ets-in-port">
					{formatEuEtsEmissionCost(
						estimate.euaPriceOverride ?? estimate.remoteEuaPrice,
						ensureNegative(estimate.result?.factorAdjustedEligibleEmissionsInPort ?? 0),
						estimate.currency,
					)}
				</span>
			),
		},
		{
			label: ` - Other: ${formatAndRoundNumber(estimate.result.totalEligibleEmissionsOther ?? 0, 0)} MT CO₂`,
			span: 3,
			children: (
				<span data-testid="eu-ets-other">
					{formatEuEtsEmissionCost(
						estimate.euaPriceOverride ?? estimate.remoteEuaPrice,
						ensureNegative(estimate.result?.factorAdjustedEligibleEmissionsOther ?? 0),
						estimate.currency,
					)}
				</span>
			),
		}] : []),
		] : []),
		...(!hasCargoRelet ? [{
			label: (
				<Flex justify="space-between">
					UK ETS Costs
					<Button
						type="link"
						size="small"
						onClick={() => updateExpanded('ukEts')}
						data-testid="expand-uka-costs"
					>
						{expanded.ukEts ? 'Hide' : 'Expand'}
					</Button>
				</Flex>
			),
			span: 3,
			children: (
				<span data-testid="uk-ets-costs">
					{formatCurrency(
						estimate.result.totalUKAcost ?? 0,
						estimate.currency,
					)}
				</span>
			),
		},
		...(expanded.ukEts ? [{
			label: ` - At Sea: ${formatAndRoundNumber(
				estimate.result.totalUkEligibleEmissionsAtSea ?? 0,
				0,
			)} MT CO₂`,
			span: 3,
			children: formatCurrency(
				(estimate.result.totalUkEligibleEmissionsAtSea ?? 0) *
				(estimate.ukaPrice ?? 0),
				estimate.currency,
			),
		},
		{
			label: ` - In Port: ${formatAndRoundNumber(
				estimate.result.totalUkEligibleEmissionsInPort ?? 0,
				0,
			)} MT CO₂`,
			span: 3,
			children: formatCurrency(
				(estimate.result.totalUkEligibleEmissionsInPort ?? 0) *
				(estimate.ukaPrice ?? 0),
				estimate.currency,
			),
		},
		{
			label: ` - Other: ${formatAndRoundNumber(
				estimate.result.totalUkEligibleEmissionsOther ?? 0,
				0,
			)} MT CO₂`,
			span: 3,
			children: formatCurrency(
				(estimate.result.totalUkEligibleEmissionsOther ?? 0) *
				(estimate.ukaPrice ?? 0),
				estimate.currency,
			),
		}] : []),
		] : []),
		...(!hasCargoRelet ? [{
			label: 'FuelEU penalty',
			span: 3,
			children: (
				<span data-testid="fueleu-penalty">
					{formatCurrency(estimate.result.fuelEuPenalty ?? 0, estimate.currency)}
				</span>
			),
		}] : []),
		{
			label: 'Other Costs',
			span: 3,
			children: (
				<span data-testid="other-costs">
					{formatCurrency(estimate.result.otherVoyageExpenses ?? 0, estimate.currency)}
				</span>
			),
		},
		{
			label: (<b>Total Voyage Exp</b>),
			span: 3,
			children: (
				<b data-testid="total-voyage-exp">
					{formatCurrency(estimate.result.totalVoyageExpenses ?? 0, estimate.currency)}
				</b>
			),
		},
		...(!hasCargoRelet ? [{
			label: 'TCE Earnings',
			span: 3,
			children: (
				<span data-testid="tce-earnings">
					{formatCurrency(estimate.result.voyageProfit ?? 0, estimate.currency)}
				</span>
			),
		}] : []),
		...(!hasCargoRelet ? [{

			label: (<b>Total Days</b>),
			span: 3,
			children: (
				<b data-testid="total-days">
					{formatAndRoundNumber((estimate.result.estimatedDuration ?? 0), 2)}
				</b>
			),

		}] : []),
		...(showTargetControls ? [{
			label: (
				<Flex justify="space-between" className={styles.highlight}>
					<span>TCE /day</span>
					<TargetControls
						name="targetTce"
						label="Target TCE"
						shortcut="tce-target"
						isLocked={estimate.isTceLocked}
						target={estimate.targetTce}
						onLock={onLockTce}
						onSetTarget={onSetTargetTce}
						disabled={disabled || estimate.isNetResultLocked}
						inputDisabled={cargos.length === 0}
					/>
				</Flex>
			),
			span: 3,
			children: (
				<div className={styles.highlight}>
					<span style={{ whiteSpace: 'nowrap' }}>
						{tceDiff !== 0 && (
							<span
								style={{
									marginRight: '0.5rem',
									color: tceDiff > 0 ? 'green' : 'red',
									fontSize: '0.70rem',
								}}
							>
								{tceDiff > 0 ? '↑' : '↓'}
								{formatCurrency(Math.abs(tceDiff), estimate.currency)}
							</span>
						)}
						{' '}
						<span data-testid="tce">{formatCurrency(estimate.result.tce ?? 0, estimate.currency)}</span>
					</span>
				</div>
			),
		}] : []),
		...(!hasCargoRelet ? [{
			label: 'CO2 Emissions',
			span: 3,
			children: (
				<span data-testid="co2-emissions">
					{`${formatAndRoundNumber(estimate.result.estimatedEmissions ?? 0, 0)} MT`}
				</span>
			),
		}] : []),
		...(!hasCargoRelet ? [{
			label: 'Net Hire Cost',
			span: 3,
			children: (
				<span data-testid="net-hire-cost">
					{formatCurrency(
						(estimate.result.tcInExpense ?? 0),
						estimate.currency,
					)}
				</span>
			),
		}] : []),
		{
			label: showTargetControls ? (
				<Flex justify="space-between" className={styles.highlight}>
					<span>Net Result</span>
					<TargetControls
						name="targetNetResult"
						label="Target Net Result"
						isLocked={estimate.isNetResultLocked}
						target={estimate.targetNetResult}
						onLock={onLockNetResult}
						onSetTarget={onSetTargetNetResult}
						disabled={disabled || estimate.isTceLocked}
						inputDisabled={cargos.length === 0}
					/>
				</Flex>
			) : (<div className={styles.highlight}>Net Result</div>),
			span: 3,
			children: (
				<div className={styles.highlight}>
					<span
						data-testid="net-result"
						className={classNames({
							[styles.green]: (estimate.result.netProfit ?? 0) > 0,
							[styles.red]: (estimate.result.netProfit ?? 0) < 0,
						})}
					>
						{formatCurrency(estimate.result.netProfit ?? 0, estimate.currency)}
					</span>
				</div>
			),
		},
		...(!hasCargoRelet ? [{
			label: (
				<div className={styles.highlight}>
					<span>
						Net Result /day
					</span>
				</div>
			),
			span: 3,
			children: (
				<div className={styles.highlight}>
					<span
						data-testid="net-result-per-day"
						className={classNames({
							[styles.green]: (estimate.result.netProfitPerDay ?? 0) > 0,
							[styles.red]: (estimate.result.netProfitPerDay ?? 0) < 0,
						})}
					>
						{formatCurrency(
							estimate.result.netProfitPerDay ?? 0,
							estimate.currency,
						)}
					</span>
				</div>
			),
		}] : []),
		...(breakevenCargo != null && !hasCargoRelet && estimate.result.breakevenFreight != null ? [{
			label: 'Breakeven Freight',
			span: 3,
			children: (
				<span data-testid="breakeven-freight">
					{formatFreightRate(estimate.result.breakevenFreight, breakevenCargo)}
				</span>
			),
		}] : []),
		...(!hasCargoRelet && estimate.result.apsTce != null ? [{
			label: 'APS TCE /day',
			span: 3,
			children: (
				<span data-testid="aps-tce">
					{formatCurrency(estimate.result.apsTce, estimate.currency)}
				</span>
			),
		}] : []),
	];
};

export default getEstimatedVcPnlItems;
